import React from 'react';
import * as Sentry from "@sentry/react";
import { TextField, FormLabel, Paper } from '@mui/material';
import APIRequest, { APIError } from "../../system/APIRequest";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SimpleFormDialog from 'components/dialogs/SimpleFormDialog';
import { DashboardPageInterface } from 'routes/dashboard/DashboardPage';
import { useDashboardPageList } from 'routes/dashboard/Dashboard';
import { getDashboardNavigatePath, parsePageParams } from 'components/resources/dashboards/DashboardNavigationUtils';

interface PageEditDialogOptions {
  activePageID?: string | null;
  activePageName?: string | null;
  dashboard: DashboardDocument;
  onSetLocalDashboardPages: (newPages: DashboardPageInterface[]) => void;
  handleUpdatePageName?: (name: string) => void;
  // Base URL to navigate back to on changes, defaults to /dashboard/:id
  navigationRoot?: string;
  onClose?: () => void;
  setDialogIsOpen?: (value: boolean) => void;
}

export default function PageEditDialog(props: PageEditDialogOptions): JSX.Element {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { dashboardPages } = useDashboardPageList();

  const navigationRoot = props.navigationRoot || `/dashboard/${props.dashboard?.id}`;

  const [/* pageData */, setPageData] = useState(null); // TODO: Node for future use 
  const [pageTitle, setPageTitle] = useState<string>("");

  // function that gets a page via the API
  const getPage = () => {
    if (!props.activePageID) {
      Sentry.captureMessage("Unable to get page: no page ID");
      return;
    }

    APIRequest(`obj/dashboard-page/${props.activePageID}`, {}).then((response: any) => {
      setPageData(response.output);
      setPageTitle(response.output.name);
    }).catch((error: APIError) => {
      console.error(`Error getting page: ${error}`);
      Sentry.captureException(error);
    });
  }

  // function that updates an existing page via the API
  const updatePage = () => {
    if (!props.activePageID) {
      Sentry.captureMessage("Unable to update page: no page ID");
      return;
    }

    // Optimistically rerender the local dashboard state & UI
    const foundPage = dashboardPages.find(pages => pages.id === props.activePageID);
    if (foundPage) {
      const foundPageIndex = dashboardPages.indexOf(foundPage);
      const foundPageCopy = { ...foundPage, name: pageTitle };
      const newPages = [...dashboardPages];
      newPages[foundPageIndex] = foundPageCopy;
      props.onSetLocalDashboardPages(newPages);
    }

    APIRequest(`obj/dashboard-page/${props.activePageID}`, {}, {
      method: "PUT",
      "body": JSON.stringify({
        name: pageTitle,
      })
    }).then((response: any) => {
      props.onClose && props.onClose();
      props.setDialogIsOpen && props.setDialogIsOpen(false);
      if (props.handleUpdatePageName) {
        props.handleUpdatePageName(pageTitle);
      }
    }).catch((error: APIError) => {
      // If optimistic rerender fails, reset to previous dashboardPages
      props.onSetLocalDashboardPages(dashboardPages);
      console.error(`Error updating page: ${error}`);
      Sentry.captureException(error);
    });
  }

  // function that creates a new page via the API
  const createNewPage = () => {
    if (!props.dashboard?.id) {
      Sentry.captureMessage("Unable to create new page: no dashboard ID");
      return;
    }

    const newUUID = self.crypto.randomUUID();
    const requestBody:any = {
      name: pageTitle,
      uuid: newUUID,
      dashboard_id: props.dashboard?.id
    };

    APIRequest("obj/dashboard-page", {}, {
      method: "POST",
      "body": JSON.stringify(requestBody)
    }).then((response: any) => {
      // We now need to add the page to the dashboard
      const dashboardData = props.dashboard?.data || {};
      if (!dashboardData.pages) {
        dashboardData.pages = [];
      }
      dashboardData.pages.push(newUUID);

      APIRequest(`obj/dashboard/${props.dashboard?.id}`, {}, {
        method: "PUT",
        "body": JSON.stringify({
          data: dashboardData
        })
      }).then((innerResponse: any) => {
        props.onClose && props.onClose();
        props.setDialogIsOpen && props.setDialogIsOpen(false);

        if (response.id) {
          const pageParams = parsePageParams(); // Get url
          pageParams.pageId = response.id; // Use new page id as "pageId"
          // Get new url w/ query, if query is present, then navigate to new page
          const navigatePath = getDashboardNavigatePath(pageParams);
          window.location.href = navigatePath;
        }
      }).catch((error: APIError) => {
        console.error(`Error updating dashboard: ${error}`);
        Sentry.captureException(error);
      });
    }).catch((error: APIError) => {
      console.error(`Error creating new dashboard: ${error}`);
      Sentry.captureException(error);
    });
  }

  useEffect(() => {
    if (props.activePageID) {
      getPage();
    }
  }, [props.activePageID]);

  return (
    <SimpleFormDialog
      isOpen={true}
      title={
        <>
          {!props.activePageID && i18n.t("dashboard.edit-pages.title-add-page")}
          {props.activePageID && i18n.t("dashboard.edit-pages.title-edit-page")}
        </>
      }
      onCancel={() => {
        props.onClose && props.onClose();
        props.setDialogIsOpen && props.setDialogIsOpen(false);
      }}
      onConfirm={() => {
        props.activePageID ? updatePage() : createNewPage();
      }}
      confirmText={
        <>
          {!props.activePageID && i18n.t("dashboard.edit-pages.confirm-create-page")}
          {props.activePageID && i18n.t("dashboard.edit-pages.confirm-update-page")}
        </>
      }
    >
      <Paper sx={{ p: 2, pt: 1.5 }}>
        <FormLabel>{i18n.t("dashboard.edit-pages.page-title")}:</FormLabel>
        <TextField
          autoComplete="off"
          fullWidth
          name="title"
          placeholder={i18n.t("dashboard.edit-pages.page-title-placeholder")}
          onChange={(event) => setPageTitle(event.target.value)}
          value={pageTitle}
          variant="outlined"
        />
      </Paper>
    </SimpleFormDialog>
  );
}
